"use client";

import { useEffect, useState, useRef, memo } from "react";

/**
 * NetworkPulse — A subtle, barely-perceptible living background effect.
 * Simulates network load by slowly modulating gradient intensity,
 * creating the sensation that the site is a living organism
 * connected to the fiber-optic backbone.
 *
 * Mobile optimizations:
 * - Canvas resolution halved (0.5x device pixels)
 * - Throttled to ~15fps (vs 60fps on desktop)
 * - Fewer gradient layers (skip center pulse)
 * - Visibility check: pauses when tab is hidden
 */

/* Simulated network load — oscillates between 0.3 and 1.0 */
function useNetworkLoad(isMobile: boolean) {
  const [load, setLoad] = useState(0.5);

  useEffect(() => {
    let frame: number;
    let lastUpdate = 0;
    const start = performance.now();
    const interval = isMobile ? 66 : 0; // ~15fps on mobile, every frame on desktop

    const tick = () => {
      const now = performance.now();
      if (isMobile && now - lastUpdate < interval) {
        frame = requestAnimationFrame(tick);
        return;
      }
      lastUpdate = now;

      const t = (now - start) / 1000;
      /* Composite of multiple sine waves for organic feel */
      const wave1 = Math.sin(t * 0.15) * 0.25; // Very slow primary
      const wave2 = Math.sin(t * 0.37 + 1.2) * 0.12; // Slightly faster
      const wave3 = isMobile ? 0 : Math.sin(t * 0.73 + 2.8) * 0.06; // Skip micro-ripple on mobile
      const noise = isMobile ? 0 : Math.sin(t * 2.1) * 0.03; // Skip noise on mobile

      setLoad(Math.max(0.3, Math.min(1.0, 0.55 + wave1 + wave2 + wave3 + noise)));
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isMobile]);

  return load;
}

export const NetworkPulse = memo(function NetworkPulse() {
  // Lazy init: reads window / documentElement only on client. SSR returns defaults.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [isLight, setIsLight] = useState<boolean>(() =>
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("data-theme") === "light"
      : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    // Watch for theme changes
    const obs = new MutationObserver(() => {
      setIsLight(document.documentElement.getAttribute("data-theme") === "light");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => { window.removeEventListener("resize", check); obs.disconnect(); };
  }, []);

  const load = useNetworkLoad(isMobile);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let frame: number;
    let lastDraw = 0;
    const drawInterval = isMobile ? 66 : 0; // ~15fps on mobile
    const scale = isMobile ? 0.5 : 1; // Half resolution on mobile

    const draw = () => {
      const now = performance.now();
      if (isMobile && now - lastDraw < drawInterval) {
        frame = requestAnimationFrame(draw);
        return;
      }
      lastDraw = now;

      const w = Math.round(window.innerWidth * scale);
      const h = Math.round(window.innerHeight * scale);

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }

      ctx.clearRect(0, 0, w, h);

      /* Reduce intensity in light mode */
      const intensity = isLight ? 0.4 : 1.0;

      /* Primary blue pulse — top-right */
      const r1 = Math.max(w, h) * 0.6;
      const g1 = ctx.createRadialGradient(
        w * 0.8,
        h * 0.2,
        0,
        w * 0.8,
        h * 0.2,
        r1
      );
      g1.addColorStop(0, `rgba(0, 102, 255, ${0.04 * load * intensity})`);
      g1.addColorStop(0.5, `rgba(0, 102, 255, ${0.015 * load * intensity})`);
      g1.addColorStop(1, "transparent");
      ctx.fillStyle = g1;
      ctx.fillRect(0, 0, w, h);

      /* Teal accent pulse — bottom-left */
      const g2 = ctx.createRadialGradient(
        w * 0.15,
        h * 0.75,
        0,
        w * 0.15,
        h * 0.75,
        r1 * 0.8
      );
      g2.addColorStop(0, `rgba(0, 212, 170, ${0.025 * load * intensity})`);
      g2.addColorStop(0.5, `rgba(0, 212, 170, ${0.01 * load * intensity})`);
      g2.addColorStop(1, "transparent");
      ctx.fillStyle = g2;
      ctx.fillRect(0, 0, w, h);

      /* Center subtle warm pulse — skip on mobile */
      if (!isMobile) {
        const g3 = ctx.createRadialGradient(
          w * 0.5,
          h * 0.5,
          0,
          w * 0.5,
          h * 0.5,
          r1 * 0.5
        );
        g3.addColorStop(0, `rgba(0, 80, 200, ${0.012 * load * intensity})`);
        g3.addColorStop(1, "transparent");
        ctx.fillStyle = g3;
        ctx.fillRect(0, 0, w, h);
      }

      frame = requestAnimationFrame(draw);
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [load, isMobile, isLight]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{
        opacity: 1,
        mixBlendMode: isLight ? "multiply" : "screen",
        /* CSS scales the low-res canvas back to full viewport on mobile */
        imageRendering: isMobile ? "auto" : undefined,
      }}
      aria-hidden="true"
    />
  );
});
NetworkPulse.displayName = "NetworkPulse";
